import { useMemo } from "react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { getIncidentById } from "../../helpers/apis";
import { IncidentModal } from "./IncidentModal";
import { IncidentList } from "./IncidentList";
import { Summary } from "./Summary";
import styles from "./styles.module.scss";
import { Card, CardContent, Stack } from "@altimateai/lego";
import { Paginate } from "../../components/paginate";
import { DebouncedInput } from "../../components/form";
import { useIncidentFilter } from "../../services/filters";
import { executeOnlyIfNumber } from "./helper";
import { ComponentLoader } from "../../components/loader";
import { FilterTagWrapper, Tags } from "../../components/tags";
import { NoResource } from "../../components/noResource";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { IncidentsTracking } from "@/lib/tracking/incidents";
import type { Filter } from "../../components/tags/types";

const GET_INCIDENTS_ID = "GET-INCIDENTS-ID";

interface FilterOption {
  label: string;
  value: string | string[];
}

interface ContractFilterProps {
  getTrueValues: () => string[];
  removeFilter: (removeLabel: string, contractOptions: FilterOption[]) => void;
}

interface TimeFilterProps {
  getLabel: () => string;
  onChange: (value: string) => void;
}

interface LevelFilterItem {
  value: string;
  label: string;
}

interface LevelFilterProps {
  getLabel: () => LevelFilterItem[];
  onChange: (value: string) => void;
}

interface FilterWidgetProps {
  isFilter: boolean | string | string[];
  contractFilter: ContractFilterProps;
  timeFilter: TimeFilterProps;
  levelFilter: LevelFilterProps;
  contractOptions: FilterOption[];
}

const FilterWidget = ({
  isFilter,
  contractFilter,
  timeFilter,
  levelFilter,
  contractOptions,
}: FilterWidgetProps) => {
  if (!isFilter) return null;

  const contractFilterTags: Record<string, Filter> = {
    durationFilter: {
      label: "Duration",
      filterStr: timeFilter.getLabel(),
      onclose: () => timeFilter.onChange(""),
    },
    contractFilter: {
      label: "Contracts",
      filterStr: contractFilter
        .getTrueValues()
        .map(
          (value: string) =>
            contractOptions.find((option: FilterOption) =>
              Array.isArray(option.value) ? option.value.includes(value) : option.value === value
            )?.label ?? value
        ),
      onclose: (removeKey?: string) => {
        if (removeKey !== undefined) {
          contractFilter.removeFilter(removeKey, contractOptions);
        }
      },
    },
  };
  levelFilter.getLabel().forEach((i: LevelFilterItem) => {
    contractFilterTags[i.value] = {
      label: "Level",
      filterStr: i.label,
      onclose: () => levelFilter.onChange(i.value),
    };
  });
  return (
    <Tags>
      <FilterTagWrapper filters={contractFilterTags} />
    </Tags>
  );
};

const Incidents = () => {
  const { incidentId, eventId } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  let {
    incidents,
    isFilter,
    sort,
    timeFilter,
    levelFilter,
    paginate,
    contractFilter,
    contractOptions,
    isLoading,
  } = useIncidentFilter({ eventId });

  useBreadcrumb("Incidents");

  const { data: incidentById, isLoading: isIncidentByIdLoading } = useQuery({
    queryKey: [GET_INCIDENTS_ID, search],
    queryFn: () => {
      if (!search) return;
      return getIncidentById(search);
    },
  });

  if (search && incidentById) {
    incidents = [incidentById];
    paginate = {
      ...paginate,
      itemCount: 1,
      pageSize: 1,
      numPages: 1,
    };
  }

  const [recentIncidentIds, setRecentIncidentIds] = useState({});
  const isIncident = typeof incidentId !== "undefined";
  useMemo(() => {
    setRecentIncidentIds(m => (isIncident ? { ...m, [incidentId]: true } : {}));
  }, [incidentId, isIncident]);

  const toggle = () => navigate("/incidents");
  const onIncidentClick = (id: number | string) => {
    IncidentsTracking.detailViewed(String(id));
    navigate(`/incidents/${id}`);
  };

  if (paginate.itemCount === 0 && !isFilter) {
    return (
      <NoResource
        title="No Incident Found"
        message="This means either all of your data contract validations have
              passed successfully or there are some issues with your onboarding
              configuration."
      />
    );
  }

  return (
    <>
      <Stack direction="column" gap="none" className="al-h-full">
        <h3 className="mb-4 text-black">Incidents</h3>
        <Summary />
        {isLoading || isIncidentByIdLoading ? (
          <ComponentLoader top={60} left={60} label="Loading incidents..." />
        ) : (
          <>
            <div className={styles.listing}>
              <Card>
                <CardContent className="al-pt-4">
                  <Stack justify="between" align="center">
                    <Stack gap="lg" align="center">
                      <div className="text-black fs-4">Incidents</div>
                      <FilterWidget
                        isFilter={isFilter}
                        levelFilter={levelFilter}
                        contractFilter={contractFilter}
                        timeFilter={timeFilter}
                        contractOptions={contractOptions}
                      />
                    </Stack>
                    <div className="invisible">
                      <DebouncedInput
                        size="sm"
                        placeholder="Search"
                        aria-label="Search incidents by ID"
                        onChange={executeOnlyIfNumber((value: string) => {
                          setSearch(value);
                          if (value) {
                            IncidentsTracking.searched(value);
                          }
                        })}
                      />
                    </div>
                  </Stack>

                  <IncidentList
                    incidents={incidents ?? []}
                    sort={sort}
                    timeFilter={timeFilter}
                    levelFilter={levelFilter}
                    contractFilter={{
                      options: contractOptions.map(opt => ({
                        label: opt.label,
                        value: Array.isArray(opt.value) ? opt.value.join(",") : opt.value,
                      })),
                      value: contractFilter.getValue,
                      onChange: contractFilter.toggleValue,
                    }}
                    onIncidentClick={onIncidentClick}
                  />
                  <Paginate {...paginate} />
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </Stack>
      {isIncident && (
        <IncidentModal
          isOpen={isIncident}
          incidentId={incidentId}
          recentIncidentIds={recentIncidentIds}
          toggle={toggle}
          onIncidentClick={onIncidentClick}
        />
      )}
    </>
  );
};

export { Incidents, FilterWidget };
